import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Typography, 
    Chip, 
    TablePagination,
    Skeleton,
    Button
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { FinanceService } from '../../../api/services/financeService';
import ExportLedgerModal from './ExportLedgerModal';
import { formatCurrency } from '../../../utils/currency';
import { extractTotalCount, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function FinanceTransactionsTab() {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    // Pagination
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                limit: limit
            };

            const response = await FinanceService.getFinanceTransactions(params);
            
            const dataArray = response?.data?.data || response?.data || response?.transactions || [];
            setTransactions(Array.isArray(dataArray) ? dataArray : []);
            setTotalRecords(extractTotalCount(response, dataArray));
        } catch (error) {
            console.error('Failed to fetch finance transactions:', error);
            // Handled via global interceptor
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, [page, limit]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    
    const handleChangeRowsPerPage = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'N/A';
        return new Date(isoString).toLocaleString('en-US', { 
            year: 'numeric', month: 'short', day: 'numeric', 
            hour: '2-digit', minute: '2-digit' 
        });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ReceiptLongIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold">
                        Transaction History Ledger
                    </Typography>
                </Box>
                <Button 
                    variant="outlined" 
                    startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                    onClick={() => setExportModalOpen(true)}
                    sx={standardSecondaryButtonSx}
                >
                    Download Reporting File
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Transaction ID</TableCell>
                            <TableCell>Posting Datetime</TableCell>
                            <TableCell>Description / Reference</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from(new Array(limit)).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell><Skeleton width="80%" /></TableCell>
                                    <TableCell><Skeleton width="90%" /></TableCell>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell align="right"><Skeleton width="40%" sx={{ ml: 'auto' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : transactions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                    <Typography color="text.secondary">No transactional data recorded matching this view.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            transactions.map((txn, idx) => (
                                <TableRow key={txn.transactionId || idx} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                            {txn.transactionId ? txn.transactionId.substring(0, 8).toUpperCase() : 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(txn.date)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {txn.description || 'N/A'}
                                        </Typography>
                                        {txn.reference && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                Ref: {txn.reference.substring(0, 8)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {txn.category || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={txn.type || 'N/A'} 
                                            size="small" 
                                            color={(txn.type === 'Credit' || txn.type === 'CREDIT') ? 'success' : 'error'} 
                                            variant="outlined"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={txn.status || 'N/A'} 
                                            size="small" 
                                            color={txn.status === 'Cleared' ? 'success' : txn.status === 'Voided' ? 'error' : 'warning'} 
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography 
                                            variant="body2" 
                                            fontWeight="bold" 
                                            color={(txn.type === 'Credit' || txn.type === 'CREDIT') ? 'success.main' : 'error.main'}
                                        >
                                            {txn.type === 'Debit' || txn.type === 'DEBIT' ? '-' : '+'}{formatCurrency(txn.amount)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                
                <TablePagination
                    component="div"
                    count={transactions.length === 0 ? 0 : totalRecords}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <ExportLedgerModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
            />
        </Box>
    );
}
